/**
 * Accessibility enhancements applied at runtime.
 *
 * Everything here targets markup this repo does not own: the esbuild bundle in
 * assets/theme.expanded.js (built from sources outside this repo) and the cart
 * drawer injected by the Zipify OCU app. Editing either in place would be
 * overwritten by the next build or app update, so the fixes are re-applied to
 * the live DOM instead. Anything that can be fixed in Liquid is fixed in Liquid.
 *
 * Nothing here changes layout, styling or behaviour for pointer users. The
 * announcer is visually hidden; the rest sets ARIA attributes and moves keyboard
 * focus only while a dialog is open.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Polite announcer
   * ------------------------------------------------------------------ */

  var announcer = null;

  function getAnnouncer() {
    if (announcer && document.body && document.body.contains(announcer)) {
      return announcer;
    }
    announcer = document.createElement('div');
    announcer.id = 'a11y-live-announcer';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    // Visually hidden, but NOT display:none or visibility:hidden — either would
    // stop assistive tech reading the region at all.
    announcer.style.cssText =
      'position:absolute !important;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0;';
    document.body.appendChild(announcer);
    return announcer;
  }

  function announce(message) {
    if (!message) return;
    var region = getAnnouncer();
    // Clearing first makes a repeat of the same string announce again.
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  /* ------------------------------------------------------------------ *
   * Cart additions
   *
   * Adding to cart used to be entirely silent: the drawer is app-rendered and
   * no live region was written to, so a screen reader user got no confirmation
   * that anything happened. Rather than patch whichever app owns the drawer,
   * watch the authoritative number — Shopify's own cart — and announce when it
   * grows. This keeps working if the cart UI is swapped out.
   * ------------------------------------------------------------------ */

  var lastKnownCount = null;
  var cartCheckPending = false;

  function readCart() {
    return window
      .fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function describeNewest(cart) {
    if (!cart || !cart.items || !cart.items.length) return '';
    var item = cart.items[0];
    var name = item.product_title || item.title || '';
    if (name && item.variant_title && item.variant_title !== 'Default Title') {
      name += ', ' + item.variant_title;
    }
    return name;
  }

  function checkCart(isInitial) {
    if (cartCheckPending) return;
    cartCheckPending = true;

    readCart().then(function (cart) {
      cartCheckPending = false;
      if (!cart) return;

      var count = cart.item_count;

      if (isInitial || lastKnownCount === null) {
        lastKnownCount = count;
        return;
      }

      if (count > lastKnownCount) {
        var product = describeNewest(cart);
        var itemWord = count === 1 ? 'item' : 'items';
        announce(
          (product ? 'Added to cart: ' + product + '. ' : 'Added to cart. ') +
            'Cart now has ' + count + ' ' + itemWord + '.'
        );
      } else if (count < lastKnownCount) {
        announce(
          count === 0
            ? 'Item removed. Your cart is now empty.'
            : 'Item removed. Cart now has ' + count + (count === 1 ? ' item.' : ' items.')
        );
      }

      lastKnownCount = count;
    });
  }

  var cartCheckTimer = null;
  function scheduleCartCheck() {
    window.clearTimeout(cartCheckTimer);
    // The cart UI and the cart API settle a moment after the click; this also
    // collapses the burst of DOM mutations a drawer re-render produces.
    cartCheckTimer = window.setTimeout(function () {
      checkCart(false);
    }, 450);
  }

  function watchForCartActivity() {
    // Any click on something that adds to or changes the cart is a cheap,
    // app-agnostic trigger. The cart read is what decides whether to announce,
    // so a false trigger costs one request and says nothing.
    document.addEventListener(
      'click',
      function (event) {
        var trigger = event.target.closest(
          '[name="add"], [type="submit"], .ocu-cart-drawer button, .ocu-cart-drawer a, ' +
            'cart-drawer button, cart-items button, [data-cart-remove], .cart-remove-button'
        );
        if (trigger) scheduleCartCheck();
      },
      true
    );

    document.addEventListener('submit', scheduleCartCheck, true);

    // Themes and apps both broadcast these; harmless if nothing fires them.
    ['cart:updated', 'cart:refresh', 'cart:change', 'ocu:cart:updated'].forEach(function (name) {
      document.addEventListener(name, scheduleCartCheck);
    });

    // Clicks and events are heuristics: a theme or app that posts to the cart
    // without a matching click target, or that swallows the event, announces
    // nothing. Every path to the cart goes through one of these endpoints, so
    // watching the request itself is the only trigger that cannot be missed.
    watchCartRequests();
  }

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function watchCartRequests() {
    if (window.__a11yCartHooked) return;
    window.__a11yCartHooked = true;

    if (typeof window.fetch === 'function') {
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = '';
        try {
          url = typeof input === 'string' ? input : (input && input.url) || '';
        } catch (e) {
          url = '';
        }
        var result = nativeFetch.apply(this, arguments);
        if (CART_WRITE.test(url)) {
          // Announce off the settled response, never the request, so the count
          // read back is the one the server actually stored.
          result.then(scheduleCartCheck, function () {});
        }
        return result;
      };
    }

    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (CART_WRITE.test(String(url || ''))) {
        this.addEventListener('loadend', scheduleCartCheck);
      }
      return open.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------ *
   * Cart drawer dialog semantics
   *
   * The Zipify OCU drawer renders as a plain container: no role, no accessible
   * name, focus left behind on the Add to cart button, and no way to close it
   * from the keyboard. aria-modal is only set once focus is actually inside,
   * because marking a dialog modal while the user's focus sits outside it hides
   * the page from them and strands them there.
   * ------------------------------------------------------------------ */

  // The cart surface differs per store and per app: Zipify OCU injects its own
  // drawer, and this theme ships one at [data-drawer="cart"]. Dawn's own
  // <cart-drawer> is deliberately not listed — it already sets a role, a name
  // and its own focus trap, so patching it would double up.
  // Marketing popups are the same problem as the cart drawer: a plain <div>
  // thrown over the page with no role, no name, no focus move and a <div> for a
  // close control. They are listed here so one piece of machinery handles both.
  var DRAWER_SELECTORS = [
    '.ocu-cart-drawer',
    '[data-drawer="cart"] [data-drawer-content]',
    '[class*="klaviyo-form"]',
    '.zpa-crm-popup'
  ];

  // What each surface should be called once it has a dialog role.
  function dialogName(el) {
    if (el.closest('.ocu-cart-drawer') || el.matches('[data-drawer="cart"] [data-drawer-content]')) {
      return 'Your cart';
    }
    // A popup's own heading is a better name than anything invented here.
    var heading = el.querySelector('h1, h2, h3, [role="heading"]');
    var text = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
    if (text) return text.slice(0, 80);
    return 'Dialog';
  }
  var CLOSE_SELECTOR =
    '[data-cart-drawer-close],[data-drawer-close],[data-cart-close],[data-close],' +
    '[class*="close"],[aria-label*="lose"]';
  var FOCUSABLE =
    'a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),' +
    'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  var openDrawer = null;
  var focusBeforeDrawer = null;

  function isVisible(el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    if (rect.right <= 0 || rect.left >= window.innerWidth) return false;
    for (var node = el; node && node !== document.documentElement; node = node.parentElement) {
      var style = window.getComputedStyle(node);
      if (style.visibility === 'hidden' || style.display === 'none' || parseFloat(style.opacity) === 0) {
        return false;
      }
    }
    return true;
  }

  function visibleFocusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), isVisible);
  }

  function findCloseControl(container) {
    var explicit = container.querySelector(CLOSE_SELECTOR);
    if (explicit && isVisible(explicit)) return explicit;
    var focusables = visibleFocusables(container);
    return focusables.length ? focusables[0] : null;
  }

  function enhanceDrawer(drawer) {
    if (!drawer.getAttribute('role')) drawer.setAttribute('role', 'dialog');
    if (!drawer.getAttribute('aria-label') && !drawer.getAttribute('aria-labelledby')) {
      drawer.setAttribute('aria-label', dialogName(drawer));
    }

    // Name the close control if the app left it bare, and make sure a div-based
    // one is reachable and operable from the keyboard.
    var close = drawer.querySelector(CLOSE_SELECTOR);
    if (close) {
      var name = (close.getAttribute('aria-label') || close.textContent || '').trim();
      if (!name) {
        close.setAttribute('aria-label', drawer.classList.contains('ocu-cart-drawer') ? 'Close cart' : 'Close');
      }
      var tag = close.tagName.toLowerCase();
      if (tag !== 'button' && tag !== 'a') {
        if (!close.getAttribute('role')) close.setAttribute('role', 'button');
        if (!close.hasAttribute('tabindex')) close.setAttribute('tabindex', '0');
        if (!close.dataset.a11yKeyBound) {
          close.dataset.a11yKeyBound = 'true';
          close.addEventListener('keydown', function (event) {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              close.click();
            }
          });
        }
      }
    }
  }

  function onDrawerOpened(drawer) {
    if (openDrawer === drawer) return;
    openDrawer = drawer;
    focusBeforeDrawer = document.activeElement;

    enhanceDrawer(drawer);

    var target = findCloseControl(drawer);
    if (target) {
      // Only now is it safe to claim the dialog is modal.
      try {
        target.focus({ preventScroll: true });
      } catch (err) {
        target.focus();
      }
      if (drawer.contains(document.activeElement)) {
        drawer.setAttribute('aria-modal', 'true');
      }
    }
  }

  function onDrawerClosed() {
    if (!openDrawer) return;
    openDrawer.removeAttribute('aria-modal');
    var restoreTo = focusBeforeDrawer;
    openDrawer = null;
    focusBeforeDrawer = null;
    if (restoreTo && document.body.contains(restoreTo) && isVisible(restoreTo)) {
      try {
        restoreTo.focus({ preventScroll: true });
      } catch (err) {
        restoreTo.focus();
      }
    }
  }

  function currentDrawer() {
    for (var i = 0; i < DRAWER_SELECTORS.length; i++) {
      var candidates = document.querySelectorAll(DRAWER_SELECTORS[i]);
      for (var j = 0; j < candidates.length; j++) {
        if (isVisible(candidates[j])) return candidates[j];
      }
    }
    return null;
  }

  function syncDrawerState() {
    var drawer = currentDrawer();
    if (drawer) onDrawerOpened(drawer);
    else onDrawerClosed();
  }

  function watchDrawer() {
    var observer = new MutationObserver(function () {
      window.clearTimeout(watchDrawer.timer);
      watchDrawer.timer = window.setTimeout(syncDrawerState, 120);
    });
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
    });

    // Esc closes the drawer. The app binds no key handler at all, and the
    // theme's own handler is bound to an element focus never reaches.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!openDrawer) return;
      var close = openDrawer.querySelector(CLOSE_SELECTOR);
      if (close) {
        event.preventDefault();
        close.click();
      }
    });

    // Keep Tab inside the dialog while it is marked modal.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !openDrawer) return;
      if (openDrawer.getAttribute('aria-modal') !== 'true') return;

      var focusables = visibleFocusables(openDrawer);
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (!openDrawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Names for controls the bundle and apps ship without one
   * ------------------------------------------------------------------ */

  // A control is only named if something actually produces text for it. A
  // wrapping <label> that renders no text (a styled toggle switch, a bare
  // dropdown) leaves the control nameless, so "is inside a label" is not the
  // test — "has text coming from somewhere" is.
  function accessibleName(field) {
    var aria = field.getAttribute('aria-label');
    if (aria && aria.trim()) return aria.trim();
    var by = field.getAttribute('aria-labelledby');
    if (by) {
      var text = by.split(/\s+/).map(function (id) {
        var el = document.getElementById(id);
        return el ? el.textContent : '';
      }).join(' ').replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
    if (field.id) {
      var forLabel = document.querySelector('label[for="' + CSS.escape(field.id) + '"]');
      if (forLabel && forLabel.textContent.replace(/\s+/g, ' ').trim()) {
        return forLabel.textContent.replace(/\s+/g, ' ').trim();
      }
    }
    var wrapper = field.closest('label');
    if (wrapper) {
      // The options inside a <select> are its value, not its name, so they do
      // not count towards the wrapper's text.
      var clone = wrapper.cloneNode(true);
      Array.prototype.forEach.call(clone.querySelectorAll('select, input, textarea'), function (n) {
        n.parentNode.removeChild(n);
      });
      var wrapText = clone.textContent.replace(/\s+/g, ' ').trim();
      if (wrapText) return wrapText;
    }
    var title = field.getAttribute('title');
    if (title && title.trim()) return title.trim();
    return '';
  }

  function labelFromContext(field) {
    // The visible label is often a sibling or a heading in the same block, but
    // not associated with the field in markup.
    var described = field.closest('.select-menu, .subscription-toggle, [class*="frequency"]');
    if (described) {
      var caption = described.querySelector(
        '.body-caption-small, .select-label, h5, h6, label:not([for])'
      );
      if (caption) {
        var text = caption.textContent.replace(/\s+/g, ' ').trim();
        if (text) return text;
      }
    }
    return '';
  }

  // Shopify knows what each variant dropdown is for; the bundle just never
  // renders it. layout/theme.liquid publishes the product's option names and
  // values, and a select is identified by the values it offers.
  function productOptions() {
    if (productOptions.cache !== undefined) return productOptions.cache;
    var node = document.getElementById('a11y-product-options');
    var parsed = null;
    if (node) {
      try { parsed = JSON.parse(node.textContent); } catch (e) { parsed = null; }
    }
    productOptions.cache = Array.isArray(parsed) ? parsed : [];
    return productOptions.cache;
  }

  function nameFromProductOptions(field) {
    var options = productOptions();
    if (!options.length) return '';
    var texts = Array.prototype.map.call(field.options, function (o) {
      return o.text.replace(/\s+/g, ' ').trim().toLowerCase();
    }).filter(Boolean);
    if (!texts.length) return '';
    var best = '';
    var bestScore = 0;
    options.forEach(function (option) {
      if (!option || !option.name || !Array.isArray(option.values)) return;
      var values = option.values.map(function (v) {
        return String(v).replace(/\s+/g, ' ').trim().toLowerCase();
      });
      var hits = texts.filter(function (t) { return values.indexOf(t) !== -1; }).length;
      var score = hits / texts.length;
      if (score > bestScore) { bestScore = score; best = option.name; }
    });
    // Require a clear majority so an unrelated dropdown is never mislabelled.
    return bestScore >= 0.6 ? best : '';
  }

  // The nearest heading-ish text above a control, used when nothing in the
  // markup names it. Kept short so it reads as a label, not a paragraph.
  function nearestBlockLabel(field) {
    var block = field.closest('div, fieldset, section');
    for (var depth = 0; depth < 4 && block; depth++) {
      var candidate = block.querySelector('h1, h2, h3, h4, h5, h6, [class*="title"], [class*="heading"], strong, b');
      if (candidate && !candidate.contains(field)) {
        var text = candidate.textContent.replace(/\s+/g, ' ').trim();
        if (text && text.length <= 60) return text;
      }
      block = block.parentElement;
    }
    return '';
  }

  // A variant dropdown on an upsell or bundle card belongs to that card's
  // product, not the page's, so product.options_with_values will not match it.
  // The nearest heading is the wrong answer here - on a bundle card it is the
  // section title, which would label a flavour dropdown "MASA Bundles". What
  // is knowable is the card it belongs to and, from the shape of its values,
  // whether it picks a quantity. Anything less certain than that stays generic
  // rather than guessing wrong.
  function describeVariantSelect(field) {
    var texts = Array.prototype.map.call(field.options, function (o) {
      return o.text.replace(/\s+/g, ' ').trim();
    }).filter(Boolean);
    var quantityLike = texts.length > 0 && texts.every(function (t) {
      return /^\d+\s*(bags?|packs?|ct|count|pieces?|items?)?$/i.test(t);
    });
    var base = quantityLike ? 'Quantity' : 'Product option';

    var card = field.closest('[class*="product-card"], [class*="card-product"], [class*="product-item"]');
    if (card) {
      var title = card.querySelector('[class*="title"], [class*="heading"], h2, h3, h4, a[href*="/products/"]');
      var text = title ? title.textContent.replace(/\s+/g, ' ').trim() : '';
      if (text && text.length <= 60) return base + ' for ' + text;
    }
    return base;
  }

  function nameUnlabelledControls(root) {
    var scope = root && root.querySelectorAll ? root : document;

    Array.prototype.forEach.call(scope.querySelectorAll('select'), function (field) {
      if (accessibleName(field)) return;
      var name = nameFromProductOptions(field) || labelFromContext(field);
      if (!name) {
        var selected = field.options && field.options[field.selectedIndex];
        // A subscription frequency select reads "Delivery Every 4 Weeks"; without
        // a name a screen reader announces only the value, with no idea what it sets.
        if (selected && /every|week|month|day/i.test(selected.text)) {
          name = 'Delivery frequency';
        } else {
          name = describeVariantSelect(field);
        }
      }
      field.setAttribute('aria-label', name);
    });

    Array.prototype.forEach.call(
      scope.querySelectorAll('input[type="checkbox"], input[type="radio"]'),
      function (field) {
        if (accessibleName(field)) return;
        var name = labelFromContext(field) || nearestBlockLabel(field);
        if (!name && /subscri/i.test(field.className + ' ' + (field.id || ''))) {
          name = 'Subscribe and save';
        }
        if (name) field.setAttribute('aria-label', name);
      }
    );

    // An icon-only link is announced as nothing at all. The cart and account
    // links in the header are the common case, and they are on the buy path.
    Array.prototype.forEach.call(scope.querySelectorAll('a[href]'), function (link) {
      if (link.getAttribute('aria-label') || link.getAttribute('aria-labelledby')) return;
      if (link.textContent.replace(/\s+/g, ' ').trim()) return;
      if (link.querySelector('img[alt]:not([alt=""])')) return;
      var title = link.getAttribute('title');
      if (title && title.trim()) return;
      var href = link.getAttribute('href') || '';
      var icon = link.querySelector('svg, i, [class*="icon"]');
      var hints = ((icon && icon.getAttribute && icon.getAttribute('class')) || '') + ' ' + link.className + ' ' + href;
      var name = '';
      if (/\/cart|cart/i.test(hints)) name = 'Cart';
      else if (/\/account|login|sign-?in/i.test(hints)) name = 'Account';
      else if (/\/search|search|magnif/i.test(hints)) name = 'Search';
      else if (/instagram/i.test(hints)) name = 'Instagram';
      else if (/facebook/i.test(hints)) name = 'Facebook';
      else if (/tiktok/i.test(hints)) name = 'TikTok';
      else if (/youtube/i.test(hints)) name = 'YouTube';
      else if (/twitter|x\.com/i.test(hints)) name = 'X';
      else if (/pinterest/i.test(hints)) name = 'Pinterest';
      else if (href === '/' || /\/$/.test(href)) name = 'Home';
      // A link to an anchor on the same page with nothing inside it: the page
      // builders emit these as jump links next to a rating or a heading. The
      // text it sits beside is what it is for.
      if (!name && /^#./.test(href)) {
        var context = link.parentElement
          ? link.parentElement.textContent.replace(/\s+/g, ' ').trim()
          : '';
        if (context && context.length <= 80) name = context;
      }
      if (name) link.setAttribute('aria-label', name);
    });

    Array.prototype.forEach.call(scope.querySelectorAll('button'), function (button) {
      if (button.getAttribute('aria-label') || button.getAttribute('aria-labelledby')) return;
      if (button.textContent.replace(/\s+/g, ' ').trim()) return;
      if (button.querySelector('img[alt]:not([alt=""])')) return;

      // Icon-only button. Infer from the icon class where possible; otherwise
      // hide it from assistive tech is wrong (it is operable), so give a generic
      // but honest name.
      var icon = button.querySelector('[class*="fa-"], svg, i');
      var iconClass = (icon && icon.getAttribute && icon.getAttribute('class')) || '';
      // Data attributes say what a control is far more reliably than a class
      // list does, and the price slider thumbs are named entirely by theirs.
      var attrs = button.getAttributeNames().join(' ');
      var classes = iconClass + ' ' + button.className + ' ' + attrs;
      var name = '';
      if (/price-slider/.test(attrs)) {
        if (/-from-thumb/.test(attrs)) name = 'Minimum price';
        else if (/-to-thumb/.test(attrs)) name = 'Maximum price';
      }
      if (name) {
        button.setAttribute('aria-label', name);
        return;
      }
      if (/close|times|xmark/i.test(classes)) name = 'Close';
      else if (/prev|chevron-left|arrow-left|(^|\s|-)left(\s|$)/i.test(classes)) name = 'Previous';
      else if (/next|chevron-right|arrow-right|(^|\s|-)right(\s|$)/i.test(classes)) name = 'Next';
      else if (/play/i.test(classes)) name = 'Play video';
      else if (/search|magnif/i.test(classes)) name = 'Search';
      else if (/menu|bars|hamburger/i.test(classes)) name = 'Menu';
      else if (/cart|bag/i.test(classes)) name = 'Cart';
      // The gallery arrows are one chevron rotated in opposite directions, so
      // the rotation is the only thing that distinguishes them.
      else if (/-rotate-90/.test(iconClass)) name = 'Previous image';
      else if (/rotate-90/.test(iconClass)) name = 'Next image';
      if (name) button.setAttribute('aria-label', name);
    });
  }

  // Section background colours chosen inside the Zipify Pages editor, one page
  // at a time, across dozens of landing pages. A stylesheet cannot reach them:
  // the colour is not attached to any class that means "this colour", it is set
  // per section, and the section classes are shared with pages whose colours
  // already pass.
  //
  // So the swap is done by value. This is an enumerated list of exact colours,
  // each with a hand-picked replacement of the same hue at the darkness that
  // clears 4.5:1 - not a scan-and-guess. A colour not on this list is never
  // touched, and only elements the builder rendered are looked at.
  //
  // The proper fix is to change these in the Zipify editor. Once that happens
  // the entry stops matching anything and can be deleted.
  var CONTRAST_SWAP = {
    // white text sits on these blues, at 4.41:1 and 4.11:1
    'rgb(68, 125, 171)': '#4075a0',
    'rgb(69, 125, 171)': '#4075a0',
    'rgb(76, 130, 174)': '#45759d',
    // white text on Zipify's copy of the MASA salmon, 2.50:1
    'rgb(249, 130, 99)': '#b55e48',
    'rgb(248, 130, 100)': '#aa5945',
    // The Alia widget holds its own copy of the pre-2026-09-16 brand blue and
    // paints its button white on it - 4.39:1, on every page at mobile widths.
    // Its colour is set in the Alia admin; this is the same swap the theme
    // already made, applied until it is changed there.
    'rgb(69, 125, 171)': '#4175a0'
  };

  // `[class*="oke-"]` does NOT match Okendo's LOYALTY widget: its classes are
  // okeLoyalty / c-button--customisable / c-customisableActionCard, with no
  // "oke-" substring. So the salmon swap below, which has been correcting this
  // exact colour elsewhere on the site for weeks, never reached /pages/loyalty
  // - where white on rgb(248,130,100) measures 2.50:1 across "Join or Log In"
  // and seven "Go" buttons.
  var BUILDER_SCOPE =
    '[class*="zpa-"], [class*="zp-"], [class*="tsl-"], [class*="oke-"], [data-rid], [id^="alia-"], ' +
    '[class*="okeLoyalty"], [class*="c-customisable"], [class*="c-button"], ' +
    // Attribute substring selectors are CASE-SENSITIVE, and Okendo mixes the
    // two conventions in one widget: c-button--customisable but
    // c-faqCustomisableItem. Matching only the lower-case spelling left the
    // loyalty FAQ cards - white on the PRE-2026 brand blue #457dab, 4.39:1 -
    // outside the swap while the buttons beside them were corrected.
    '[class*="c-faq"], [class*="Customisable"]';

  function swapFailingColours(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(BUILDER_SCOPE), function (el) {
      if (el.dataset.a11yColourSwapped) return;
      var styles = window.getComputedStyle(el);
      var swapped = '';
      var bg = CONTRAST_SWAP[styles.backgroundColor];
      if (bg) {
        el.style.setProperty('background-color', bg, 'important');
        swapped += 'bg';
      }
      var fg = CONTRAST_SWAP[styles.color];
      if (fg) {
        el.style.setProperty('color', fg, 'important');
        swapped += 'fg';
      }
      if (swapped) el.dataset.a11yColourSwapped = swapped;
    });
  }

  /* ------------------------------------------------------------------ *
   * Skip link
   *
   * layout/theme.liquid renders <a href="#MainContent"> as the first thing in
   * the body and <main id="MainContent" tabindex="-1"> as its target, which is
   * all a skip link normally needs. It does not work on its own here: the
   * theme's variant router rewrites the URL (?variant=...&selling_plan=...)
   * and drops the fragment with it, so the browser never performs the
   * fragment navigation and focus stays on the link - verified live, the next
   * Tab went straight back to the announcement bar.
   *
   * So move focus explicitly. Nothing else about the link changes.
   * ------------------------------------------------------------------ */

  function wireSkipLink() {
    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest
        ? event.target.closest('.a11y-skip-link')
        : null;
      if (!link) return;
      var id = (link.getAttribute('href') || '').replace(/^#/, '');
      var target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'start' });
    });
  }

  /* ------------------------------------------------------------------ *
   * PDP flavour chips
   *
   * The bundle paints each chip with an inline custom property
   * (--highlight-sibling: #f88264) and hard-codes the label to white, which
   * is 1.36:1 on the White chip and 2.50:1 on Original. The seven flavours
   * live in stores are handled by a stylesheet rule in
   * assets/a11y-enhancements.css, keyed on that same inline property so a
   * re-render cannot discard it. This is the part that keeps working when a
   * flavour is added later: read the chip colour, work out whether black or
   * white text carries further on it, and stamp the answer on the element for
   * the stylesheet to act on.
   * ------------------------------------------------------------------ */

  function relativeLuminance(r, g, b) {
    var channel = function (v) {
      v /= 255;
      return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  }

  function labelFlavourChips(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var chips = scope.querySelectorAll('span.label--md[style*="--highlight-sibling"]');
    Array.prototype.forEach.call(chips, function (chip) {
      var raw = chip.style.getPropertyValue('--highlight-sibling').trim();
      var hex = /^#([0-9a-f]{6})$/i.exec(raw);
      if (!hex) return;
      var n = parseInt(hex[1], 16);
      var l = relativeLuminance((n >> 16) & 255, (n >> 8) & 255, n & 255);
      // Against near-black #1F242E (L 0.0175) and white (L 1).
      var onDark = (l + 0.05) / (0.0175 + 0.05);
      var onWhite = 1.05 / (l + 0.05);
      var want = onDark >= onWhite ? 'dark' : 'light';
      if (chip.getAttribute('data-a11y-chip') !== want) {
        chip.setAttribute('data-a11y-chip', want);
      }

      // An UNSELECTED chip is dimmed, and opacity composites BOTH the label
      // and the chip toward the page behind them - so a pair that clears 4.5:1
      // at full strength can still fail as rendered. Hatch Chile is the case:
      // #2b8148 with white is 4.84:1, and at 0.9 it renders 4.03:1.
      // Pass 2 raised this dim from .7 to .9 for the same reason; rather than
      // move it again for every chip, the dim is dropped only for a chip that
      // actually fails with it, which is one of the seven.
      var label = want === 'dark' ? [31, 36, 46] : [255, 255, 255];
      var base = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
      var o = parseFloat(window.getComputedStyle(chip).opacity);
      if (!(o > 0) || o >= 1) return;
      var mix = function (c) { return c.map(function (v) { return o * v + (1 - o) * 255; }); };
      var lf = relativeLuminance.apply(null, mix(label));
      var lb = relativeLuminance.apply(null, mix(base));
      var hi = Math.max(lf, lb), lo = Math.min(lf, lb);
      if ((hi + 0.05) / (lo + 0.05) < 4.5) {
        chip.style.setProperty('opacity', '1', 'important');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Search and collection cards whose image link has no name
   *
   * A card is an <article data-card> holding an image link and, below it, the
   * title as separate text. When the result is a page rather than a product
   * the image is a placeholder SVG with no alt, so the link that wraps it is
   * in the tab order with nothing to announce - WCAG 2.4.4 / 4.1.2. The title
   * is right there in the card, so name the link from it.
   * ------------------------------------------------------------------ */

  function nameEmptyCardLinks(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-card]'), function (card) {
      var links = card.querySelectorAll('a[href]');
      if (!links.length) return;
      var title = '';
      Array.prototype.some.call(card.querySelectorAll('h1,h2,h3,h4,h5,h6,[data-card-title]'), function (el) {
        title = (el.textContent || '').replace(/\s+/g, ' ').trim();
        return !!title;
      });
      if (!title) {
        title = (card.textContent || '').replace(/\s+/g, ' ').trim().split(' View More')[0];
      }
      if (!title) return;
      Array.prototype.forEach.call(links, function (link) {
        if (link.getAttribute('aria-label') || link.getAttribute('title')) return;
        if ((link.textContent || '').trim()) return;
        if (link.querySelector('img[alt]:not([alt=""])')) return;
        link.setAttribute('aria-label', title.slice(0, 120));
      });
    });
  }

  // Images the page builders render with no alt attribute at all. Zipify and
  // Replo hold alt in their own editors and expose no API, so the text lives
  // in assets/a11y-image-alt.js, keyed by the filename the builder serves.
  // This only ever fills in a MISSING attribute - anything the builder does
  // set, including an empty alt on a decorative image, is left exactly alone.
  function applyAuthoredAlt(root) {
    var map = window.__a11yImageAlt;
    if (!map) return;
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('img:not([alt])'), function (img) {
      var src = img.getAttribute('src') || img.currentSrc || '';
      if (!src) {
        var set = img.getAttribute('srcset') || '';
        src = set.split(',')[0].trim().split(/\s+/)[0] || '';
      }
      if (!src) return;
      var name = '';
      try {
        name = decodeURIComponent(new URL(src, location.href).pathname.split('/').pop() || '');
      } catch (e) {
        name = src.split('?')[0].split('/').pop() || '';
      }
      if (!name) return;
      var alt = map[name.toLowerCase()];
      if (alt === undefined) return;
      img.setAttribute('alt', alt);
    });
  }

  // Carousel and marquee libraries re-parent list items into their own <div>
  // wrappers, which silently breaks the ul > li relationship: the list stops
  // being a list and the items stop being items, so a screen reader no longer
  // announces "list, 21 items" (WCAG 1.3.1). The DOM cannot be put back
  // without fighting the library, but ARIA roles restore the semantics over
  // whatever nesting it produced.
  function repairWrappedLists(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('ul, ol'), function (list) {
      if (list.getAttribute('role')) return;
      var items = list.querySelectorAll('li');
      if (!items.length) return;
      // Only lists a library has interfered with: a direct child that is not
      // an <li>, script or template.
      var interfered = Array.prototype.some.call(list.children, function (child) {
        var tag = child.tagName.toLowerCase();
        return tag !== 'li' && tag !== 'script' && tag !== 'template';
      });
      if (!interfered) return;
      list.setAttribute('role', 'list');
      Array.prototype.forEach.call(items, function (item) {
        if (!item.getAttribute('role')) item.setAttribute('role', 'listitem');
      });
    });
  }

  // Apps inject iframes for prefetching, chat and tracking and rarely title
  // them. An untitled frame is announced as "frame" with no way to tell what
  // it is (WCAG 4.1.2). One that is hidden or has no size is not content at
  // all, so it is taken out of the accessibility tree rather than named.
  function titleUntitledFrames(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('iframe, frame'), function (frame) {
      var title = frame.getAttribute('title');
      if (title && title.trim()) return;
      if (frame.getAttribute('aria-hidden') === 'true') return;
      var rect = frame.getBoundingClientRect();
      var styles = window.getComputedStyle(frame);
      var invisible =
        rect.width < 2 ||
        rect.height < 2 ||
        styles.display === 'none' ||
        styles.visibility === 'hidden';
      if (invisible) {
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        return;
      }
      var host = '';
      try {
        host = new URL(frame.src, location.href).hostname.replace(/^www\./, '');
      } catch (e) {
        host = '';
      }
      var known = {
        'youtube.com': 'YouTube video',
        'youtube-nocookie.com': 'YouTube video',
        'player.vimeo.com': 'Vimeo video',
        'vimeo.com': 'Vimeo video'
      };
      frame.setAttribute('title', known[host] || (host ? 'Embedded content from ' + host : 'Embedded content'));
    });
  }

  // A container that scrolls but cannot be focused is unreachable by keyboard:
  // there is no way to scroll it without a pointer (WCAG 2.1.1).
  function makeScrollablesFocusable(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-scroll-container], [class*="overflow-x-auto"], [class*="overflow-y-auto"], [class*="overflow-auto"]'),
      function (region) {
        if (region.hasAttribute('tabindex')) return;
        if (region.querySelector('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')) return;
        var scrollsX = region.scrollWidth > region.clientWidth + 1;
        var scrollsY = region.scrollHeight > region.clientHeight + 1;
        if (!scrollsX && !scrollsY) return;
        region.setAttribute('tabindex', '0');
        if (!region.getAttribute('role')) region.setAttribute('role', 'group');
        if (!accessibleName(region) && !region.getAttribute('aria-label')) {
          region.setAttribute('aria-label', 'Scrollable content');
        }
      }
    );
  }


  // A link whose visible text renders but whose ACCESSIBLE NAME computes to
  // nothing announces as "link" and nothing else (WCAG 2.4.4 / 4.1.2). Okendo's
  // rating summary is the live case: the review count sits inside a <figure>
  // of stars that contributes no name, so the PDP's jump-to-reviews link is the
  // only unnamed link on the page. The visible text is the right name, so it is
  // simply promoted to an aria-label rather than invented.
  function nameTextlessLinks(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('a[href]'), function (link) {
      if (link.getAttribute('aria-label') || link.getAttribute('aria-labelledby')) return;
      if (link.getAttribute('title')) return;
      var text = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) return;
      // Text that lives only inside aria-hidden or a <figure>/<svg> does not
      // reach the name. Anything contributing directly is fine and left alone.
      var named = Array.prototype.some.call(link.childNodes, function (node) {
        if (node.nodeType === 3) return !!node.textContent.trim();
        if (node.nodeType !== 1) return false;
        if (node.getAttribute('aria-hidden') === 'true') return false;
        var tag = node.tagName.toLowerCase();
        if (tag === 'figure' || tag === 'svg' || tag === 'img') return false;
        return !!(node.textContent || '').trim();
      });
      if (named) return;
      var label = /^\d+\s+reviews?$/i.test(text) ? 'Read ' + text : text;
      link.setAttribute('aria-label', label.slice(0, 120));
    });
  }

  // WCAG 1.1.1: a star rating drawn as SVG says nothing to a screen reader.
  // The theme's React rating (assets/product.rating.tsx, compiled into
  // theme.js) draws each star with fill="url(#star-N)", N the percent filled
  // rounded UP to 5, and prints only "462 reviews" beside them - so a sighted
  // shopper sees 4.7 stars and a screen-reader user hears a count. This is the
  // exact defect cited in the 2026-09-17 demand letter. The rating is read
  // back from the fills: N is ceil(fraction*20)*5, so the true score lies in
  // (total-0.05, total] and flooring to one decimal recovers Okendo's own
  // rounded figure (4.75 drawn -> 4.7, which is what Okendo prints).
  function labelDrawnRatings(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var groups = [];
    Array.prototype.forEach.call(scope.querySelectorAll('svg[fill^="url(#star-"]'), function (svg) {
      var box = svg.parentElement;
      if (box && groups.indexOf(box) === -1) groups.push(box);
    });
    groups.forEach(function (box) {
      var stars = Array.prototype.filter.call(box.children, function (el) {
        return /^url\(#star-\d+\)$/.test(el.getAttribute('fill') || '');
      });
      if (stars.length < 3) return;
      var total = 0;
      stars.forEach(function (el) {
        total += +((el.getAttribute('fill') || '').match(/\d+/) || [0])[0];
      });
      var rating = Math.floor(total / 10) / 10;
      var label = rating > 0
        ? 'Rated ' + rating + ' out of ' + stars.length + ' stars'
        : 'No ratings yet';
      if (box.getAttribute('aria-label') !== label) {
        box.setAttribute('role', 'img');
        box.setAttribute('aria-label', label);
      }
      // A link around the figure takes its name from aria-label, which
      // nameTextlessLinks set to the count alone. Put the score in front of
      // it; the visible count stays in the name (WCAG 2.5.3).
      var link = box.closest('a[href]');
      if (!link) return;
      var current = link.getAttribute('aria-label') || '';
      if (current && !/^(read )?(\d[\d,]*|no) reviews?$/i.test(current)) return;
      var count = (link.textContent || '').replace(/\s+/g, ' ').trim();
      var name = label + (count ? ', ' + count : '');
      if (current !== name) link.setAttribute('aria-label', name.slice(0, 120));
    });
  }

  // WCAG 1.3.1: a crossed-out price carries its meaning in the line through
  // it. Without words, "$79.00 $63.20" is two prices and no hint which one
  // the shopper pays. The React price, card and cart components draw it with
  // Tailwind's line-through and no hidden label. A DOM node inserted into a
  // React tree can be torn out on the next render (or break it), so the words
  // ride on a data attribute that a11y-enhancements.css renders as a visually
  // hidden ::before - screen readers read generated content, nothing moves.
  var MONEY = /[$€£]\s?\d/;
  function labelStruckPrices(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('.line-through, s, del, strike'), function (el) {
      if (el.hasAttribute('data-a11y-before')) return;
      if (el.children.length > 1) return;
      if (!MONEY.test(el.textContent || '')) return;
      var prev = el.previousElementSibling;
      if (prev && /price/i.test(prev.textContent || '') && !MONEY.test(prev.textContent || '')) return;
      el.setAttribute('data-a11y-before', 'Regular price ');
      var next = el.nextElementSibling;
      while (next && !MONEY.test(next.textContent || '')) next = next.nextElementSibling;
      if (next && !next.hasAttribute('data-a11y-before') && !next.matches('.line-through, s, del, strike')) {
        next.setAttribute('data-a11y-before', 'Sale price ');
      }
    });
  }

  // An alt that is a FILE NAME is read out letter for letter ("managed retail
  // masa underscore chips dot png") and describes nothing (WCAG 1.1.1). The
  // StoreLocators app does this to the logo beside every store in its list,
  // on both stores, and says "masa" on Vandy too. Such an image sits next to
  // the store's name, so it is decorative: alt="" is the correct value. The
  // one exception is an image that is the only content of a link or button,
  // where emptying the alt would leave the control unnamed.
  var FILENAME_ALT = /\.(jpe?g|png|webp|gif|svg|avif)\s*$/i;
  function clearFilenameAlts(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('img[alt]'), function (img) {
      if (!FILENAME_ALT.test(img.getAttribute('alt') || '')) return;
      var control = img.closest('a, button');
      if (control && !(control.textContent || '').trim()) return;
      img.setAttribute('alt', '');
    });
  }

  // WCAG 2.4.4: in a screen reader's links list, which strips the surrounding
  // context a sighted user reads the link through, six links all called "View
  // Product" are six identical entries pointing at six different products.
  // The fix is an aria-label that KEEPS the visible words first (WCAG 2.5.3
  // Label in Name, and so a voice-control user can still say "View Product")
  // and appends the product the card is about. An attribute, deliberately:
  // injecting a span into a card the page builder re-renders is a layout risk
  // for no gain.
  var GENERIC_LINK_TEXT = /^(view product|view bundle|shop now|shop|learn more|read more|view more|see more|view details|buy now)$/i;

  function disambiguateGenericLinks(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('a[href]'), function (link) {
      if (link.getAttribute('aria-label') || link.getAttribute('aria-labelledby')) return;
      var text = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (!GENERIC_LINK_TEXT.test(text)) return;
      var card = link.closest('.product-card, [data-card], article, li');
      if (!card) return;
      var title = '';
      Array.prototype.some.call(
        card.querySelectorAll('h1,h2,h3,h4,h5,h6,[data-card-title],[class*="title"]'),
        function (el) {
          if (el.contains(link)) return false;
          title = (el.textContent || '').replace(/\s+/g, ' ').trim();
          return !!title;
        }
      );
      if (!title || title.toLowerCase() === text.toLowerCase()) return;
      link.setAttribute('aria-label', text + ': ' + title.slice(0, 90));
    });
  }

  // role="alert" is ASSERTIVE: it interrupts whatever the screen reader is
  // part-way through saying. Okendo parks two of them on the literal text
  // "Loading..." on every product page, so any content they are later given
  // arrives as an interruption. role="status" is announced politely at the
  // next pause, which is what a review widget's messages actually warrant.
  // (Chrome currently leaves these two out of the accessibility tree
  // altogether, so this is hardening rather than a live defect - but it is
  // the app's markup, and an app update can expose them at any time.)
  function calmLoadingAlerts(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[role="alert"]'), function (region) {
      if (!region.classList.contains('oke-alert')) {
        var text = (region.textContent || '').trim();
        if (text && !/^loading\.{0,3}$/i.test(text)) return;
        if (!region.className && !text) return;
      }
      region.setAttribute('role', 'status');
    });
  }


  // Landmarks are only a bypass mechanism if there are few enough of them to
  // choose between. App sections render their own wrappers, so a product page
  // ships SIX <main> elements (one nested inside another) and SEVEN unnamed
  // <nav>s: a screen reader's rotor lists seven identical "navigation" entries
  // and several "main"s, and the user cannot tell which is the page (1.3.1).
  //
  // The ROLE is changed, never the tag. A <main> that stops being a landmark
  // still matches every `main ...` CSS selector on the page - and there is at
  // least one on the PDP, driving the gallery's first-image fade - so retagging
  // would be a visual change where an ARIA role is not.
  function deduplicateLandmarks(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var mains = scope.querySelectorAll('main');
    if (mains.length > 1) {
      var keeper = document.getElementById('MainContent');
      if (!keeper || !document.contains(keeper)) keeper = mains[0];
      Array.prototype.forEach.call(mains, function (el) {
        if (el === keeper) {
          if (el.getAttribute('role') === 'presentation') el.removeAttribute('role');
          return;
        }
        if (!el.getAttribute('role')) el.setAttribute('role', 'presentation');
      });
    }

    Array.prototype.forEach.call(scope.querySelectorAll('nav'), function (nav) {
      if (nav.getAttribute('aria-label') || nav.getAttribute('aria-labelledby')) return;
      var name = '';
      var heading = nav.querySelector('h1,h2,h3,h4,h5,h6');
      if (heading) name = (heading.textContent || '').replace(/\s+/g, ' ').trim();
      if (!name) {
        if (nav.closest('footer')) name = 'Footer';
        else if (nav.closest('header')) name = 'Main';
        else if (nav.querySelector('[aria-current], .breadcrumb, [class*="breadcrumb"]')) name = 'Breadcrumb';
      }
      if (!name) {
        var link = nav.querySelector('a[href]');
        name = link ? (link.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 30) : '';
      }
      nav.setAttribute('aria-label', name ? name + ' navigation' : 'Navigation');
    });
  }

  // Every page needs exactly one top-level heading, and on a product page it
  // has to be the product. Masa's shipped the wrong one: the PDP carried two
  // <h1>s and BOTH read "MASA Bundles", from an upsell card that renders in
  // the cart drawer on every page, while the product's own name was an <h2>.
  // Pressing 1 or H to orient landed the reader in an upsell.
  //
  // The cards are fixed in Liquid (they are ours). The page's own title comes
  // out of the page builder, so if a page ends up with no h1 at all, the
  // highest heading inside the main content is promoted with ARIA - a level,
  // not a tag, so nothing about the rendering moves.
  // A card title is never the page. These are the theme's own React components
  // (product-card-flat, product-card-flat-wide, variant_upsell), and one of
  // them renders inside the slide cart, so a single stray <h1> reading "MASA
  // Bundles" shipped on EVERY page - twice on the product page, where they
  // were the only h1s it had. The .tsx sources are fixed, but assets/theme.js
  // is a COMMITTED COMPILED ARTIFACT with no build step in this repo, so the
  // source fix does not reach the page until someone rebuilds. Until then the
  // level is corrected at runtime: aria-level, not a tag, so nothing moves.

  // Two ARIA bugs shipped by apps, both of which make a checker report a
  // CRITICAL failure and neither of which does anything useful on the page.
  //
  // `aria-role` is not an ARIA attribute - it is a misspelling of `role`, so
  // whatever the app meant to say is simply not said. Okendo's loyalty
  // carousel (Swiper) writes it on every slide: 13 of them on /pages/loyalty.
  // The value is moved to the real attribute where one is missing, then the
  // misspelling is removed.
  //
  // An aria-label on a plain <div> with no role is PROHIBITED and ignored, so
  // "Slide controls" never reaches anyone. Giving the element a generic
  // container role makes its own label valid, which is what the app intended.
  function fixAppAriaBugs(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[aria-role]'), function (el) {
      var intended = (el.getAttribute('aria-role') || '').trim();
      if (intended && !el.getAttribute('role')) el.setAttribute('role', intended);
      el.removeAttribute('aria-role');
    });
    Array.prototype.forEach.call(
      scope.querySelectorAll('[class*="okeLoyalty"] div[aria-label]:not([role]), [class*="c-customisable"][aria-label]:not([role]), .swiper div[aria-label]:not([role])'),
      function (el) { el.setAttribute('role', 'group'); }
    );
  }

  function demoteCardHeadings(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      // The first version of this keyed on one class name and missed five more
      // card components - _collection-card, _page-card, _content-card,
      // _product-card--small and featured-articles.article - plus the compiled
      // content-card.tsx and product-card-small.tsx. The Liquid ones are fixed
      // at source; the compiled pair render `inline-flex pt-2`, which is what
      // puts two <h1>s into Masa's SEARCH RESULTS.
      scope.querySelectorAll('h1[class*="product-card-title"], h1[class*="article-card-title"], article h1[class*="inline-flex"][class*="pt-2"], h1[class*="inline-flex"][class*="pt-2"]'),
      function (el) {
        if (el.getAttribute('aria-level') === '3') return;
        el.setAttribute('role', 'heading');
        el.setAttribute('aria-level', '3');
      }
    );
  }

  function ensureTopLevelHeading(root) {
    if (root && root !== document) return;
    var existing = document.querySelectorAll('h1, [role="heading"][aria-level="1"]');
    for (var j = 0; j < existing.length; j++) {
      if (existing[j].getAttribute('aria-level') === '1' || !/card-title/.test(existing[j].className || '')) return;
    }
    var main = document.getElementById('MainContent') || document.querySelector('main') || document.body;
    var candidates = main.querySelectorAll('h2, h3, [class*="product-title"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || text.length > 120) continue;
      if (el.closest('[hidden], [aria-hidden="true"], dialog, [role="dialog"]')) continue;
      var rect = el.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) continue;
      el.setAttribute('role', 'heading');
      el.setAttribute('aria-level', '1');
      return;
    }
  }

  function watchForNewControls() {
    var timer = null;
    var pendingSince = 0;
    // A trailing-only debounce starves on a page that never stops mutating.
    // The home page review marquee animates by MOVING its <li> nodes, which
    // re-parents them out of the <ul> and then keeps firing mutations, so the
    // 200ms timer was cleared before it could ever run: measured on the live
    // page, the list roles were sometimes restored at once and sometimes not
    // for 12 SECONDS, and an axe run that landed in that window reported 22
    // failures on a page that is otherwise clean. So the debounce now has a
    // ceiling: however busy the page is, the passes run at least this often.
    var MAX_WAIT = 800;

    function run() {
      window.clearTimeout(timer);
      timer = null;
      pendingSince = 0;
      nameUnlabelledControls(document);
      makeScrollablesFocusable(document);
      titleUntitledFrames(document);
      repairWrappedLists(document);
      applyAuthoredAlt(document);
      swapFailingColours(document);
      labelFlavourChips(document);
      nameEmptyCardLinks(document);
      nameTextlessLinks(document);
      labelDrawnRatings(document);
      labelStruckPrices(document);
      clearFilenameAlts(document);
      disambiguateGenericLinks(document);
      calmLoadingAlerts(document);
      fixAppAriaBugs(document);
      deduplicateLandmarks(document);
      demoteCardHeadings(document);
      ensureTopLevelHeading(document);
    }

    var observer = new MutationObserver(function () {
      var now = Date.now();
      if (!pendingSince) pendingSince = now;
      if (now - pendingSince >= MAX_WAIT) {
        run();
        return;
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(run, 200);
    });
    observer.observe(document.documentElement, { subtree: true, childList: true });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    getAnnouncer();
    wireSkipLink();
    checkCart(true);
    watchForCartActivity();
    watchDrawer();
    nameUnlabelledControls(document);
    makeScrollablesFocusable(document);
    titleUntitledFrames(document);
    repairWrappedLists(document);
    applyAuthoredAlt(document);
    swapFailingColours(document);
    labelFlavourChips(document);
    nameEmptyCardLinks(document);
    nameTextlessLinks(document);
    labelDrawnRatings(document);
    labelStruckPrices(document);
    clearFilenameAlts(document);
    disambiguateGenericLinks(document);
    calmLoadingAlerts(document);
    fixAppAriaBugs(document);
    deduplicateLandmarks(document);
    demoteCardHeadings(document);
    ensureTopLevelHeading(document);
    watchForNewControls();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
